"use strict";
(function () {
  if (!customElements.get("collage-slider")) {
    /**
     *
     * @class CollageSlider
     * @extends HTMLElement
     *
     * @example
     * <collage-slider
     *   data-slider-length="6"
     *   data-slides-per-view="3"
     *   data-slides-per-view-tb="2"
     *   data-slides-per-view-mb="1"
     *   data-space-between="20px"
     *   data-space-between-tb="16px"
     *   data-space-between-mb="12px"
     *   data-loop="true"
     *   data-autoplay="false"
     *   data-rotate="5"
     * >
     *   <div data-component="slider">
     *     <div class="swiper-wrapper">
     *       <div class="swiper-slide">Slide 1</div>
     *       <div class="swiper-slide">Slide 2</div>
     *     </div>
     *   </div>
     *   <div class="swiper-scrollbar"></div>
     *   <button class="swiper-button-prev"></button>
     *   <button class="swiper-button-next"></button>
     * </collage-slider>
     */

    class CollageSlider extends HTMLElement {
      constructor() {
        super();
        /** @type {HTMLElement|null} */
        this.slider = null;
        /** @type {Swiper|null} */
        this.swiper = null;
        /** @type {HTMLElement|null} */
        this.scrollbar = null;
      }

      /**
       * Called when the element is inserted into the DOM.
       * Initializes all UI elements and creates the Swiper instance.
       * @returns {void}
       */
      connectedCallback() {
        this.setupElements();
        this.initSwiper();
      }

      /**
       * Called when the element is removed from the DOM.
       * Properly cleans up the Swiper instance to prevent memory leaks.
       * @returns {void}
       */
      disconnectedCallback() {
        if (this.swiper) {
          this.swiper.destroy();
          this.swiper = null;
        }
      }

      /**
       * Sets up references to DOM elements needed for slider functionality.
       * Queries for the main slider container, scrollbar, and all navigation buttons.
       * This method supports multiple button pairs throughout the page.
       * @returns {void}
       */
      setupElements() {
        // Main slider container that will house all slides
        this.slider = this.querySelector('[data-component="slider"]');

        // Optional scrollbar for visual feedback
        this.scrollbar = this.querySelector(".swiper-scrollbar");

        // Note: We query for button groups but pass selector strings to Swiper
        // This allows Swiper to handle all matching buttons automatically
        this.btnNexts = this.querySelectorAll(".swiper-button-next");
        this.btnPrevs = this.querySelectorAll(".swiper-button-prev");

        // Validate that critical elements exist
        if (!this.slider) {
          console.warn(
            "CollageSlider: Missing slider container with [data-component='slider']",
          );
        }
      }

      /**
       * Retrieves all original (non-cloned) slides from the slider.
       * Uses the data-cloned attribute to differentiate original slides from clones.
       * @returns {Array<HTMLElement>} Array of original slide elements
       */
      getOriginalSlides() {
        const swiperWrapper = this.slider.querySelector(".swiper-wrapper");
        if (!swiperWrapper) {
          console.warn("CollageSlider: Missing .swiper-wrapper element");
          return [];
        }

        // Get all slides that don't have the data-cloned attribute
        return Array.from(
          swiperWrapper.querySelectorAll(".swiper-slide:not([data-cloned])"),
        );
      }

      /**
       * Calculates how many times slides should be cloned for smooth infinite looping.
       * Ensures there are enough slides so Swiper can seamlessly loop without
       * showing the same content repeating abruptly.
       *
       * @param {number} originalCount - Total number of original slides
       * @param {number} maxSlidesPerView - Maximum slides visible at any breakpoint
       * @returns {number} Number of times to clone the entire slide set (0 if no cloning needed)
       *
       * @description
       * Formula: We need at least maxSlidesPerView * 1.5 slides total
       * If we have fewer, we calculate how many complete sets we need to clone
       * Example: 3 original slides, maxSlidesPerView=4
       * - minRequired = ceil(4 * 1.5) = 6 slides total needed
       * - timesToClone = ceil(6 / 3) = 2 sets = 2 complete clones
       * - return 2 - 1 = 1 (clone once, since we already have the original set)
       */
      calculateCloneCount(originalCount, maxSlidesPerView) {
        // Minimum slides required for proper looping visualization
        const minRequired = Math.ceil(maxSlidesPerView * 1.5);

        // If we already have enough slides, no cloning needed
        if (originalCount >= minRequired) {
          return 0;
        }

        // Calculate how many complete sets we need to reach the minimum
        const timesToClone = Math.ceil(minRequired / originalCount);

        // Return total clones needed (-1 because original set already exists)
        return timesToClone - 1;
      }

      /**
       * Creates and appends cloned copies of all original slides to the wrapper.
       * Each clone is marked with the data-cloned attribute for identification.
       * This is essential for infinite loop functionality with limited content.
       *
       * @param {number} timesToClone - How many times to clone the entire slide set
       * @returns {void}
       *
       * @description
       * Process:
       * 1. If timesToClone <= 0, exit early (no cloning needed)
       * 2. Get the swiper wrapper and all original slides
       * 3. For each clone iteration, duplicate all original slides
       * 4. Mark each clone with data-cloned="true" attribute
       * 5. Append to the wrapper (cloned slides go after originals)
       *
       * Safety checks prevent errors if wrapper or slides don't exist.
       */
      cloneSlides(timesToClone) {
        // Early exit if no cloning required
        if (timesToClone <= 0) return;

        const swiperWrapper = this.slider.querySelector(".swiper-wrapper");
        const originalSlides = this.getOriginalSlides();

        // Exit safely if no slides found
        if (originalSlides.length === 0) {
          console.warn("CollageSlider: No original slides found to clone");
          return;
        }

        // Clone slides the specified number of times
        for (let i = 0; i < timesToClone; i++) {
          originalSlides.forEach((slide) => {
            // Create a deep copy of the slide
            const clone = slide.cloneNode(true);

            // Mark this as a cloned slide for later identification
            clone.setAttribute("data-cloned", "true");

            // Add to the wrapper
            swiperWrapper.appendChild(clone);
          });
        }

        console.log(
          `CollageSlider: Cloned slides ${timesToClone} times (original: ${originalSlides.length} slides)`,
        );
      }

      /**
       * Initializes the Swiper carousel with responsive breakpoints and configurations.
       * Handles slide cloning, responsive parameters, and navigation setup.
       *
       * @returns {void}
       *
       * @description
       * Configuration flow:
       * 1. Extract all data attributes from the custom element
       * 2. Parse and validate values (convert pixel strings to numbers, etc.)
       * 3. Ensure slidesPerView never exceeds total slides available
       * 4. Clone slides if loop is enabled and content is limited
       * 5. Create Swiper instance with three responsive breakpoints:
       *    - Mobile: < 750px
       *    - Tablet: 750px - 990px
       *    - Desktop: 990px+
       * 6. Configure navigation to work with ALL matching buttons on page
       *
       * @requires Swiper library must be loaded before this runs
       */
      initSwiper() {
        // Exit if slider element wasn't found
        if (!this.slider) return;

        // ========== EXTRACT DATA ATTRIBUTES ==========
        let {
          spaceBetween,
          spaceBetweenMb,
          spaceBetweenTb,
          slidesPerView,
          slidesPerViewTb,
          slidesPerViewMb,
          sliderLength,
          autoplay,
          loop,
          rotate,
        } = this.dataset;

        // ========== PARSE AND VALIDATE DATA ==========
        // Convert loop from string to boolean
        loop = loop === "true";

        // Convert rotate to milliseconds
        rotate = +rotate * 1000;

        // Setup autoplay configuration
        autoplay =
          autoplay == "true"
            ? {
                delay: rotate,
                disableOnInteraction: false,
              }
            : false;

        // Convert string values to numbers (remove 'px' units where needed)
        sliderLength = +sliderLength;
        spaceBetween = +spaceBetween.replace("px", "");
        spaceBetweenMb = +spaceBetweenMb.replace("px", "");
        spaceBetweenTb = +spaceBetweenTb.replace("px", "");

        // Ensure slidesPerView never exceeds available slides
        slidesPerView =
          slidesPerView > sliderLength ? sliderLength : +slidesPerView;
        slidesPerViewTb =
          slidesPerViewTb > sliderLength ? sliderLength : +slidesPerViewTb;
        slidesPerViewMb =
          slidesPerViewMb > sliderLength ? sliderLength : +slidesPerViewMb;

        // ========== HANDLE SLIDE CLONING FOR LOOPS ==========
        // If loop is enabled and slides are limited, clone slides for seamless looping
        if (loop) {
          // Find the maximum slides shown at any breakpoint
          const maxSlides = Math.max(
            slidesPerView,
            slidesPerViewTb,
            slidesPerViewMb,
          );

          // Calculate how many times to clone
          const cloneCount = this.calculateCloneCount(sliderLength, maxSlides);

          // Clone slides if needed
          this.cloneSlides(cloneCount);

          // Log cloning activity for debugging
          if (cloneCount > 0) {
            console.log(
              `CollageSlider: Cloned slides ${cloneCount} times (original: ${sliderLength}, max view: ${maxSlides})`,
            );
          }
        }

        // ========== INITIALIZE SWIPER INSTANCE ==========
        // Create new Swiper with responsive configuration
        this.swiper = new Swiper(this.slider, {
          // Mobile-first slides configuration
          slidesPerView: slidesPerViewMb,
          spaceBetween: spaceBetweenMb,

          slideToClickedSlide: true,

          // Enable grab cursor on hover for better UX
          grabCursor: true,

          // Enable infinite looping (if configured)
          loop: loop,

          // Navigation setup using selector strings
          // This allows Swiper to work with ALL buttons matching these selectors
          // even if there are multiple sliders or multiple button pairs on the page
          navigation: {
            nextEl: ".swiper-button-next",
            prevEl: ".swiper-button-prev",
          },

          // Optional scrollbar for visual feedback
          scrollbar: {
            el: this.scrollbar,
            draggable: true,
          },

          // Responsive breakpoints for different screen sizes
          breakpoints: {
            // Tablet breakpoint (750px and up)
            750: {
              slidesPerView: slidesPerViewTb,
              spaceBetween: spaceBetweenTb,
            },
            // Desktop breakpoint (990px and up)
            990: {
              slidesPerView: slidesPerView,
              spaceBetween: spaceBetween,
            },
          },

          // Autoplay configuration (if enabled)
          autoplay,
        });
      }
    }

    // Register the custom element
    customElements.define("collage-slider", CollageSlider);
  }
})();
